'use client';
import classNames from 'classnames';
import { DisplayWhenHasModules } from 'components.layouts/five-up/five-up-utils.js';
import { ModuleArrLoad } from 'layout/module-load/module-load-arr.js';
import { ModuleLoad } from 'layout/module-load/module-load.js';
import { ModulesLazyLoadWrapper } from 'layout/module-load/modules-lazy-load-wrapper.js';
import { getModulesMetaData } from 'layout/module-load/modules-meta-data.js';
import { filterOutHiddenModules, hideHiddenModules } from 'layout/utils-hide-when.js';
import { ScreenSizeContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { IModuleConfigState, SecondaryModulesState } from 'redux-state/modules/types.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { isLargeView, isThreeCol, SCREEN_SIZES } from 'utilities/media-queries.js';
import { ModulePath } from 'utilities/modulePath/modulePath.js';
import { StickyLayoutSecondarySlots } from 'utilities/sticky/stickySlots.js';
import { WarningMobileSeverityProvider } from 'utilities/weather-warnings/context/context-warning-severity.js';
import { SecondaryList } from './secondary-list.js';
import { SecondaryLargeModules } from './secondary-modules-large.js';

export interface ISecondarySectionProps {
  showPromo?: boolean;
}

export type ISecondaryCoreProps = ISecondarySectionProps & {
  columnLayout: SCREEN_SIZES;
  secondaryConfig: Record<keyof SecondaryModulesState, IModuleConfigState[]>;
};

export const SecondaryLayout = ({ showPromo }: ISecondarySectionProps) => {
  const columnLayout = useContext(ScreenSizeContext);
  const secondaryConfig = useSelector((state: IRepoState) => state.SECONDARY_MODULES);
  return <SecondaryLayoutCore columnLayout={columnLayout} secondaryConfig={secondaryConfig} showPromo={showPromo} />;
};

export const SecondaryLayoutCore = ({ showPromo, columnLayout, secondaryConfig }: ISecondaryCoreProps) => {
  const isLarge = isLargeView(columnLayout);

  const displaySecondaryMajor = filterOutHiddenModules(
    columnLayout,
    secondaryConfig.secondaryMajor.map((m) => m.data),
  ).filter(hideHiddenModules);

  const displaySecondaryMinor = filterOutHiddenModules(
    columnLayout,
    secondaryConfig.secondaryMinor.map((m) => m.data),
  ).filter(hideHiddenModules);

  const displaySecondaryRight = filterOutHiddenModules(
    columnLayout,
    secondaryConfig.secondaryRight.map((m) => m.data),
  ).filter(hideHiddenModules);

  if (isLarge) {
    return (
      <div className="Grid Grid--withGutter u-flex">
        <div className="Grid-cell u-xl-width2of12 u-lg-width3of12 Sidebar-wrapper">
          <StickyLayoutSecondarySlots isSticky={true} offset={1} classes={'Sidebar'} type="ul">
            <>
              <SecondaryList navItems={displaySecondaryMajor} />
              {displaySecondaryMinor.length > 0 && <li className="Sidebar-item Sidebar-item--spacer" />}
              <SecondaryList navItems={displaySecondaryMinor} />
            </>
          </StickyLayoutSecondarySlots>
        </div>

        <div className="Grid-cell u-lg-width9of12 u-xl-width7of12">
          <div className="Slot Slot--secondary-sidebar" data-slot-name="major">
            <SecondaryLargeModules moduleConfigArr={displaySecondaryMajor} modulePath={ModulePath.SECONDARY_MAJOR} />
            {!isThreeCol(columnLayout) && (
              <StickyLayoutSecondarySlots
                isSticky={true}
                offset={1}
                classes={'Grid-cell u-lg-mT-md Sidebar-ad'}
                type="div"
              >
                <ModulesLazyLoadWrapper
                  section={getModulesMetaData('secondary-layout-sm-right', displaySecondaryRight)}
                >
                  <ModuleArrLoad modules={displaySecondaryRight} modulePath={ModulePath.SECONDARY_RIGHT} />
                </ModulesLazyLoadWrapper>
              </StickyLayoutSecondarySlots>
            )}
          </div>

          <div className={classNames('Slot u-mT-responsive', { HasPromoPadding: showPromo })} data-slot-name="minor">
            <SecondaryLargeModules moduleConfigArr={displaySecondaryMinor} modulePath={ModulePath.SECONDARY_MINOR} />
          </div>
        </div>

        {isThreeCol(columnLayout) && (
          <div className={classNames('Grid-cell u-xl-width3of12 u-xl-mT-0 Sidebar-ad', { HasPromoPadding: showPromo })}>
            <DisplayWhenHasModules section={displaySecondaryRight}>
              <ModulesLazyLoadWrapper section={getModulesMetaData('secondary-layout-lg-right', displaySecondaryRight)}>
                <ModuleArrLoad modules={displaySecondaryRight} modulePath={ModulePath.SECONDARY_RIGHT} />
              </ModulesLazyLoadWrapper>
            </DisplayWhenHasModules>
          </div>
        )}
      </div>
    );
  }
  return (
    <>
      <>
        <DisplayWhenHasModules section={displaySecondaryRight}>
          <ul
            className={'DrawerMenu'}
            data-module-lookuparr={displaySecondaryRight.map((a) => a.name).join(' ')}
            data-slot-name="right"
          >
            {displaySecondaryRight.map((config, i) => {
              return (
                <WarningMobileSeverityProvider key={`small-${i}${config.name}`}>
                  <ModuleLoad
                    config={config}
                    isDrawer={true}
                    modulePath={ModulePath.SECONDARY_RIGHT}
                    wrapInSecondarySectionDrawer={true}
                  />
                </WarningMobileSeverityProvider>
              );
            })}
          </ul>
        </DisplayWhenHasModules>
        <DisplayWhenHasModules section={displaySecondaryMajor}>
          <>
            <ul
              className={'DrawerMenu'}
              data-module-lookuparr={displaySecondaryMajor.map((a) => a.name).join(' ')}
              data-slot-name="major"
            >
              {displaySecondaryMajor.map((config, i) => {
                return (
                  <WarningMobileSeverityProvider key={`small-${i}${config.name}`}>
                    <ModuleLoad
                      config={config}
                      isDrawer={true}
                      modulePath={ModulePath.SECONDARY_MAJOR}
                      wrapInSecondarySectionDrawer={config.hideInNav !== true}
                    />
                  </WarningMobileSeverityProvider>
                );
              })}
            </ul>
          </>
        </DisplayWhenHasModules>
      </>
      {displaySecondaryMinor.length > 0 && (
        <div className={classNames('Slot u-mB-responsive', { HasPromoPadding: showPromo })} data-slot-name="minor">
          {displaySecondaryMinor.map((config, i) => (
            <ModuleLoad key={`${config.name}-${i}`} config={config} modulePath={ModulePath.SECONDARY_MINOR} />
          ))}
        </div>
      )}
    </>
  );
};
